import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import { BASE, T, Ctx, api, inp, btn } from "./crm/shared";
import { CRM_SCREEN_ROLES, canAccessCrm } from "@/lib/roles"; // eslint-disable-line no-unused-vars
import { usePerms } from "@/context/PermissionsContext";
const Home = lazy(() => import("./crm/Home"));
const Leads = lazy(() => import("./crm/Leads"));
const Pipeline = lazy(() => import("./crm/Pipeline"));
const Queue = lazy(() => import("./crm/Queue"));
const FollowupsScreen = lazy(() => import("./crm/Queue").then((m) => ({ default: m.FollowupsScreen })));
const Rast = lazy(() => import("./crm/Rast"));
const Reports = lazy(() => import("./crm/Reports"));
const Events = lazy(() => import("./crm/Events"));
const Referrals = lazy(() => import("./crm/Referrals"));
const PartnersCredits = lazy(() => import("./crm/PartnersCredits"));
const Campaigns = lazy(() => import("./crm/Campaigns"));
const Playbook = lazy(() => import("./crm/Playbook"));
const LeadMachine = lazy(() => import("./crm/LeadMachine"));
const Earnings = lazy(() => import("./crm/Earnings"));
const Payroll = lazy(() => import("./crm/Payroll"));
const TeamReview = lazy(() => import("./crm/TeamReview"));
const Vidarbha = lazy(() => import("./crm/Vidarbha"));
const SettingsScreen = lazy(() => import("./crm/SettingsScreen"));
const MetaLeadAds = lazy(() => import("./crm/MetaLeadAds"));
const Seminars = lazy(() => import("./crm/Seminars"));
const WhatsAppConnect = lazy(() => import("./crm/WhatsAppConnect"));
const Reconcile = lazy(() => import("./crm/Reconcile"));
const Training = lazy(() => import("./crm/Training"));
const SchoolMachine = lazy(() => import("./crm/SchoolMachine"));
const Cohorts = lazy(() => import("./crm/Cohorts"));
const TwoBells = lazy(() => import("./crm/TwoBells"));
const WarRoom = lazy(() => import("./crm/WarRoom"));
const TvBoard = lazy(() => import("./crm/TvBoard"));
const Intake = lazy(() => import("./crm/Intake"));
const Incentives = lazy(() => import("./crm/Incentives"));
const DirectorDashboard = lazy(() => import("./crm/DirectorDashboard"));
const Reception = lazy(() => import("./crm/Reception"));
const TokenBoard = lazy(() => import("./crm/TokenBoard"));
const MyDay = lazy(() => import("./crm/MyDay"));
const WhatsApp = lazy(() => import("./crm/WhatsApp"));
const EventCommand = lazy(() => import("./crm/EventCommand"));
const Outreach = lazy(() => import("./crm/Outreach"));
const Chat = lazy(() => import("./crm/Chat"));
const PromiseKeeper = lazy(() => import("./crm/PromiseKeeper"));
const SeatReserve = lazy(() => import("./crm/SeatReserve"));
const CounsellingCalendar = lazy(() => import("./crm/Calendar"));
import { WaToaster } from "./crm/wa/WaToaster";
const MyLeads = lazy(() => import("./crm/MyLeads"));
const Mobility = lazy(() => import("./crm/Mobility"));
const Documents = lazy(() => import("./crm/Documents"));
const Trash = lazy(() => import("./crm/Trash"));
const BoardTv = lazy(() => import("./crm/BoardTv"));

export default function RankersCRM() {
  const [token, setToken] = useState(() => localStorage.getItem("rcrm_token"));
  const [user, setUser] = useState(null);
  const [screen, setScreen] = useState("home");
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    const link = document.createElement("link"); link.rel = "manifest"; link.href = "/crm/manifest.json"; link.id = "rcrm-manifest"; document.head.appendChild(link);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/crm/sw.js", { scope: "/crm" }).catch(() => {});
    return () => { const el = document.getElementById("rcrm-manifest"); if (el) el.remove(); };
  }, []);

  useEffect(() => {
    if (!token) { setBooting(false); return; }
    api(token).get("/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem("rcrm_token"); setToken(null); }).finally(() => setBooting(false));
  }, [token]);

  const logout = () => { localStorage.removeItem("rcrm_token"); setToken(null); setUser(null); };
  if (booting) return <div style={{ minHeight: "100vh", background: T.surface }} />;
  if (!token || !user) return <Login onToken={(t) => { localStorage.setItem("rcrm_token", t); setToken(t); }} />;

  const NAV = { Work: [["home", "Dashboard"], ["myleads", "My Leads"], ["queue", "Today's calls"], ["followups", "Follow-ups"], ["seatreserve", "Seat Reserve"], ["calendar", "Counselling Calendar"], ["chat", "Team Chat"], ["director", "Director's Dashboard"], ["mobility", "Mobility"], ["reception", "Reception"], ["token", "Token board"], ["leads", "Leads"], ["pipeline", "Pipeline"], ["rast", "RAST"], ["events", "Events"], ["eventcmd", "Event Command"], ["whatsapp", "WhatsApp"], ["schools", "Schools"], ["outreach", "School Outreach"], ["cohorts", "Reactivation"], ["bells", "Two Bells"], ["playbook", "Objections"], ["earnings", "Earnings"], ["referrals", "Referrals"], ["partners", "Partners & Credits"], ["promise", "Promise Keeper"]], Manage: [["machine", "Lead machine"], ["campaigns", "Campaigns"], ["warroom", "War Room"], ["payroll", "Payroll"], ["review", "Team review"], ["vidarbha", "Vidarbha"], ["reports", "Reports"], ["incentives", "Incentives"], ["trash", "Trash"], ["settings", "Settings"]] };
  return (
    <Ctx.Provider value={{ api: api(token), user }}>
      <div style={{ minHeight: "100vh", background: T.surface, fontFamily: "'Lexend Deca', system-ui, sans-serif", color: T.ink, display: "flex" }} data-testid="rcrm-app">
        <aside style={{ width: 236, background: T.nav, color: "#fff", padding: "18px 14px", position: "sticky", top: 0, height: "100vh", display: "flex", flexDirection: "column" }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 20, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ background: T.gold, color: T.nav, width: 28, height: 28, borderRadius: 7, display: "grid", placeItems: "center", fontWeight: 800 }}>R</span> Rankers CRM
          </div>
          <div style={{ flex: 1, overflowY: "auto", minHeight: 0, margin: "0 -6px", padding: "0 6px" }}>
            {Object.entries(NAV).map(([sec, items]) => (
              <div key={sec} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: "#8EA0C0", textTransform: "uppercase", letterSpacing: 0.5, margin: "8px 8px" }}>{sec}</div>
                {items.map(([k, label]) => (
                  <button key={k} onClick={() => setScreen(k)} data-testid={`rnav-${k}`}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                      background: screen === k ? T.sky : "transparent", color: "#fff", fontSize: 14, marginBottom: 2, fontWeight: screen === k ? 600 : 400 }}>{label}</button>
                ))}
              </div>
            ))}
          </div>
          <button onClick={logout} data-testid="rcrm-logout" style={{ flexShrink: 0, marginTop: 12, padding: "9px", borderRadius: 8, border: "1px solid #2A3B5E", background: "transparent", color: "#8EA0C0", cursor: "pointer" }}>Log out</button>
        </aside>
        <main style={{ flex: 1, minWidth: 0 }}>
          <header style={{ background: "#fff", borderBottom: `1px solid ${T.line}`, padding: "12px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 13, color: T.muted }}>The Rankers Academy · Laxmi Nagar, Nagpur</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: T.muted }}>Viewing as</span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{user.name} · {user.role}{user.band ? ` (${user.band})` : ""}</span>
            </div>
          </header>
          <div style={{ padding: 22 }}>
            {screen === "home" && <Home />}
            {screen === "queue" && <Queue />}
            {screen === "followups" && <FollowupsScreen />}
            {screen === "leads" && <Leads />}
            {screen === "intake" && <Intake />}
            {screen === "incentives" && <Incentives />}
            {screen === "pipeline" && <Pipeline />}
            {screen === "rast" && <Rast />}
            {screen === "events" && <Events />}
            {screen === "schools" && <SchoolMachine />}
            {screen === "cohorts" && <Cohorts />}
            {screen === "bells" && <TwoBells />}
            {screen === "warroom" && <WarRoom />}
            {screen === "playbook" && <Playbook />}
            {screen === "machine" && <LeadMachine />}
            {screen === "campaigns" && <Campaigns />}
            {screen === "earnings" && <Earnings />}
            {screen === "referrals" && <Referrals />}
            {screen === "training" && <Training />}
            {screen === "payroll" && <Payroll />}
            {screen === "review" && <TeamReview />}
            {screen === "vidarbha" && <Vidarbha />}
            {screen === "reports" && <Reports />}
            {screen === "settings" && <SettingsScreen />}
          </div>
        </main>
      </div>
    </Ctx.Provider>
  );
}

function Login({ onToken }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await axios.post(`${BASE}/auth/login`, { email, password }); onToken(r.data.token); toast.success(`Welcome, ${r.data.user.name}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); } finally { setBusy(false); }
  };
  return (
    <div style={{ minHeight: "100vh", background: T.nav, display: "grid", placeItems: "center", fontFamily: "'Lexend Deca', system-ui" }}>
      <form onSubmit={submit} style={{ background: "#fff", borderRadius: 14, padding: 32, width: 360 }} data-testid="rcrm-login">
        <div style={{ fontWeight: 800, fontSize: 22, color: T.ink, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: T.gold, color: T.nav, width: 32, height: 32, borderRadius: 8, display: "grid", placeItems: "center" }}>R</span> Rankers CRM
        </div>
        <p style={{ color: T.muted, fontSize: 13, marginBottom: 20 }}>Admissions command centre</p>
        <label style={{ fontSize: 12, color: T.muted }}>Email</label>
        <input data-testid="rcrm-email" value={email} onChange={(e) => setEmail(e.target.value)} style={inp} />
        <label style={{ fontSize: 12, color: T.muted }}>Password</label>
        <input data-testid="rcrm-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} style={inp} />
        <button data-testid="rcrm-login-btn" disabled={busy} style={{ ...btn(T.sky), width: "100%", marginTop: 14 }}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>
  );
}

export { CRM_SCREEN_ROLES } from "@/lib/roles";

const EMBED_SCREENS = { home: Home, queue: Queue, followups: FollowupsScreen, leads: Leads, pipeline: Pipeline, rast: Rast,
  events: Events, schools: SchoolMachine, cohorts: Cohorts, bells: TwoBells, tv: TvBoard, warroom: WarRoom, playbook: Playbook, machine: LeadMachine, campaigns: Campaigns,
  earnings: Earnings, referrals: Referrals, partners: PartnersCredits, payroll: Payroll,
  review: TeamReview, vidarbha: Vidarbha, reports: Reports, reconcile: Reconcile, training: Training,
  intake: Intake, incentives: Incentives, trash: Trash, director: DirectorDashboard,
  reception: Reception, token: TokenBoard, seatreserve: SeatReserve, calendar: CounsellingCalendar, myleads: MyLeads, mobility: Mobility, myday: MyDay, whatsapp: WhatsApp,
  eventcmd: EventCommand, outreach: Outreach, chat: Chat, promise: PromiseKeeper, settings: SettingsScreen, meta: MetaLeadAds, seminars: Seminars, waconnect: WhatsAppConnect, documents: Documents, boardtv: BoardTv };

export function CrmEmbed({ screen = "home", bare = false }) {
  const navigate = useNavigate();
  const perms = usePerms();
  const [token, setToken] = useState(() => localStorage.getItem("rcrm_token"));
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  useEffect(() => {
    let alive = true;
    (async () => {
      let t = localStorage.getItem("rcrm_token");
      if (t) {
        try { const r = await api(t).get("/me"); if (alive) { setUser(r.data); setToken(t); setStatus("ready"); } return; }
        catch { localStorage.removeItem("rcrm_token"); t = null; }
      }
      try {
        const r = await axios.post(`${BASE}/auth/bridge`, {}, { withCredentials: true });
        localStorage.setItem("rcrm_token", r.data.token);
        const me = await api(r.data.token).get("/me");
        if (alive) { setToken(r.data.token); setUser(me.data); setStatus("ready"); }
      } catch { if (alive) setStatus("error"); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => { if (user?.role) { localStorage.setItem("rcrm_role", user.role); localStorage.setItem("rcrm_uid", user.id); } }, [user]);

  useEffect(() => {
    if (status === "ready" && user && screen === "home" && user.home_view_id && !sessionStorage.getItem("rcrm_landed")) {
      sessionStorage.setItem("rcrm_landed", "1");
      navigate("/crm/leads");
    }
  }, [status, user, screen, navigate]);

  if (status === "error") return <div style={{ padding: 24, color: T.coral, fontFamily: "'Lexend Deca', system-ui" }} data-testid="rcrm-embed-error">Couldn't open the Admissions CRM for this account. Try signing out and back in.</div>;
  if (status !== "ready" || !user) return <div style={{ padding: 24, color: T.muted, fontFamily: "'Lexend Deca', system-ui" }} data-testid="rcrm-embed-loading">Loading Admissions CRM…</div>;
  if (!canAccessCrm(screen, user.role, perms.crm)) return <div style={{ padding: 24, color: T.coral, fontFamily: "'Lexend Deca', system-ui" }} data-testid="rcrm-embed-error">You don't have access to this screen. Please ask a Director.</div>;
  const Screen = EMBED_SCREENS[screen] || Home;
  if (bare) return <Ctx.Provider value={{ api: api(token), user }}><Suspense fallback={null}><Screen /></Suspense></Ctx.Provider>;
  return (
    <Ctx.Provider value={{ api: api(token), user }}>
      <div data-testid="rcrm-embed" style={{ fontFamily: "'Lexend Deca', system-ui, sans-serif", color: T.ink, background: T.surface, borderRadius: 12, padding: 18, border: `1px solid ${T.line}` }}>
        <WaToaster /><Suspense fallback={<div data-testid="crm-screen-loading" style={{ padding: 24, color: "#9A8F85", fontSize: 13 }}>Loading…</div>}><Screen /></Suspense>
      </div>
    </Ctx.Provider>
  );
}
